import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { Synced } from '../../data'
import type { NetState } from './derive'
import { problemOf } from './format'

/** Small pieces every Connectivity tab uses. */

const DOT: Readonly<Record<NetState, string>> = { online: 'is-online', offline: 'is-offline', not_joined: 'is-absent' }

export function NetDot({ state, label }: { state: NetState; label?: string }): JSX.Element {
  return <span className={`net-dot ${DOT[state]}`} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} />
}

export function Avatar({ initials, online, size = 34 }: { initials: string; online: boolean; size?: number }): JSX.Element {
  return (
    <span className="cx-avatar" style={{ width: size, height: size }} aria-hidden="true">
      {initials}
      <span className={`cx-avatar__dot ${online ? 'is-online' : 'is-offline'}`} />
    </span>
  )
}

/** A squared chip: a team, a group. `tone` warm for direct or temporary things. */
export function Chip({ children, tone = 'cool' }: { children: React.ReactNode; tone?: 'cool' | 'warm' | 'plain' }): JSX.Element {
  return <span className={`cx-chip cx-chip--${tone}`}>{children}</span>
}

/**
 * Two presses for anything that takes access away: the first arms it (the
 * control turns red and says so), the second within three seconds does it.
 * Undo cannot bring back an SSH session that was closed, so the safeguard has
 * to come before the click, not after.
 */
export function useArm(): { armed: (key: string) => boolean; press: (key: string, act: () => void) => void } {
  const [armedKey, setArmed] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])
  const press = useCallback(
    (key: string, act: () => void) => {
      if (timer.current) clearTimeout(timer.current)
      if (armedKey === key) {
        setArmed(null)
        act()
        return
      }
      setArmed(key)
      timer.current = setTimeout(() => setArmed(null), 3000)
    },
    [armedKey],
  )
  return { armed: (key) => armedKey === key, press }
}

/**
 * A choice that survives leaving the page: kept in this browser, so coming
 * back to Access shows the grid the way it was left. `allowed` guards against
 * a stored value from an older version of the screen.
 */
export function useRemembered<T extends string>(key: string, fallback: T, allowed: readonly T[]): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(key)
      return stored && (allowed as readonly string[]).includes(stored) ? (stored as T) : fallback
    } catch {
      return fallback
    }
  })
  const set = useCallback(
    (v: T) => {
      setValue(v)
      try {
        window.localStorage.setItem(key, v)
      } catch {
        // Private browsing or a full store: the choice just lasts this visit.
      }
    },
    [key],
  )
  return [value, set]
}

// --- one line saying what just happened ------------------------------------------

interface Note {
  readonly text: string
  readonly bad: boolean
}
const NoteContext = createContext<(note: Note) => void>(() => undefined)

export function NoteProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [note, setNote] = useState<Note | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const show = useCallback((n: Note) => {
    setNote(n)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setNote(null), n.bad ? 9000 : 4500)
  }, [])
  return (
    <NoteContext.Provider value={show}>
      {children}
      {note ? (
        <div className={`cx-note ${note.bad ? 'is-bad' : ''}`} role="status">
          <span>{note.text}</span>
          <button className="cx-note__close" aria-label="Dismiss" onClick={() => setNote(null)}>
            ×
          </button>
        </div>
      ) : null}
    </NoteContext.Provider>
  )
}

/**
 * Run a change, refresh the page, and say what happened — including the case
 * that matters most here: saved in Kronos but not yet on the network.
 */
export function useWrite<A extends unknown[]>(
  fn: (...args: A) => Promise<Synced>,
  said: (...args: A) => string,
): { run: (...args: A) => void; pending: boolean } {
  const queryClient = useQueryClient()
  const note = useContext(NoteContext)
  const m = useMutation({
    mutationFn: (args: A) => fn(...args),
    onSuccess: (result, args) => {
      void queryClient.invalidateQueries({ queryKey: ['connectivity'] })
      if (result.sync?.state === 'failed') note({ text: `Saved, but not on the network yet: ${result.sync.error}. Kronos retries every minute.`, bad: true })
      else note({ text: said(...args), bad: false })
    },
    onError: (e: Error) => note({ text: problemOf(e), bad: true }),
  })
  return { run: (...args: A) => m.mutate(args), pending: m.isPending }
}
